'use client';
import { FC, useEffect, useRef } from 'react';
import {
  cvaClose,
  cvaCloseIcon,
  cvaGallery,
  cvaGalleryButton,
  cvaGalleryButtonIcon,
  cvaGallerySlide,
  cvaGallerySlideBg,
  cvaGallerySlideImage,
  cvaGallerySlideLabel,
  cvaGalleryWrap,
  cvaInner,
  cvaName,
  cvaOverlay,
  cvaPagination,
  cvaRating,
  cvaRight,
  cvaRoot,
  cvaText,
} from './ReviewPopupStyles';
import { motion } from 'framer-motion';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock-upgrade';
import Rating from '@/entities/Rating/ui/Rating';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import IconClose from '/public/icons/close.svg';
import { Swiper, SwiperSlide } from 'swiper/react';
import Image from 'next/image';
import { Navigation, Pagination } from 'swiper/modules';
import IconArrow from '/public/icons/arrow-right.svg';

interface Props {
  initialIndex: number;
  rating: number;
  name: string;
  date: Date;
  text: string;
  gallery: {
    id: number;
    video: string;
    image: string;
    imageFull: string;
    before: boolean;
    after: boolean;
  }[];
  onClose: () => void;
}

const ReviewPopup: FC<Props> = ({ initialIndex = 0, rating, name, date, text, gallery, onClose }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevBtnRef = useRef<HTMLButtonElement>(null);
  const nextBtnRef = useRef<HTMLButtonElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      disableBodyScroll(scrollRef.current, {
        reserveScrollBarGap: true,
      });
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
      }}
      className={cvaRoot()}
      data-lenis-prevent
    >
      <div className={cvaOverlay()} onClick={() => onClose()}></div>
      <div className={cvaClose()} onClick={() => onClose()}>
        <IconClose className={cvaCloseIcon()} />
      </div>
      <div className={cvaInner()}>
        <div className={cvaGalleryWrap()}>
          <button
            className={cvaGalleryButton({
              direction: 'prev',
            })}
            ref={prevBtnRef}
          >
            <IconArrow
              className={cvaGalleryButtonIcon({
                direction: 'prev',
              })}
            />
          </button>
          <button
            className={cvaGalleryButton({
              direction: 'next',
            })}
            ref={nextBtnRef}
          >
            <IconArrow
              className={cvaGalleryButtonIcon({
                direction: 'next',
              })}
            />
          </button>
          <div className={cvaPagination()} ref={paginationRef}></div>
          <Swiper
            modules={[Navigation, Pagination]}
            threshold={10}
            pagination={{ clickable: true }}
            initialSlide={initialIndex}
            navigation={{
              prevEl: prevBtnRef.current,
              nextEl: nextBtnRef.current,
            }}
            onBeforeInit={(swiper: any) => {
              swiper.params.navigation.prevEl = prevBtnRef.current;
              swiper.params.navigation.nextEl = nextBtnRef.current;
            }}
            watchOverflow
            className={cvaGallery()}
          >
            {gallery?.map((item) => (
              <SwiperSlide key={item.id} className={cvaGallerySlide()}>
                <Image src={item.imageFull} className={cvaGallerySlideImage()} fill alt="" />
                <Image src={item.imageFull} className={cvaGallerySlideBg()} fill alt="" />
                {item.before || item.after ? (
                  <div
                    className={cvaGallerySlideLabel({
                      type: item.before ? 'before' : 'after',
                    })}
                  >
                    {item.before ? 'до' : 'после'}
                  </div>
                ) : null}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className={cvaRight()} ref={scrollRef}>
          <div className={cvaName()}>
            {name},{' '}
            {format(date, 'dd MMMM yyyy', {
              locale: ru,
            })}
          </div>
          <Rating className={cvaRating()} size={'small'} value={rating} />
          <div
            className={cvaText()}
            dangerouslySetInnerHTML={{
              __html: text,
            }}
          ></div>
        </div>
      </div>
    </motion.div>
  );
};

export default ReviewPopup;
